// `avatar uninstall` — Command 13 (v1.1).
// Gỡ Avatar khỏi project + auto-backup vào ~/.avatar/uninstall-backups/.
// Đối ứng với `avatar init`. Code khách (src/) giữ nguyên.

import { relative } from "node:path";
import { confirm } from "@inquirer/prompts";
import boxen from "boxen";
import type { Command } from "commander";
import { appendAuditEntry } from "../lib/audit-log-appender.js";
import { createUninstallBackupSnapshot } from "../lib/create-uninstall-backup-snapshot.js";
import { detectAvatarProjectArtifacts } from "../lib/detect-avatar-project-artifacts.js";
import { executeUninstallDeletion } from "../lib/execute-uninstall-deletion.js";
import { readCliVersion } from "../lib/read-cli-version-from-package-json.js";
import { chalk, log } from "../lib/terminal-logger.js";
import { uninstallGlobalToolsViaBrew } from "../lib/uninstall-global-tools-via-brew.js";

interface UninstallOptions {
  yes?: boolean;
  noBackup?: boolean;
  keepSubmodule?: boolean;
  keepHooks?: boolean;
  dryRun?: boolean;
}

export function registerUninstallCommand(program: Command): void {
  program
    .command("uninstall")
    .description("Gỡ Avatar khỏi project — backup tự động (M11)")
    .option("--yes", "Skip confirm prompt")
    .option("--no-backup", "Không tạo backup trước khi xóa (nguy hiểm)")
    .option("--keep-submodule", "Giữ submodule .claude/pack/")
    .option("--keep-hooks", "Giữ git hooks post-merge, pre-push")
    .option("--dry-run", "Hiển thị danh sách sẽ xóa, không thực thi")
    .action(async (opts: UninstallOptions) => {
      try {
        await runUninstall(opts);
      } catch (err) {
        log.error(err instanceof Error ? err.message : String(err));
        process.exit(1);
      }
    });
}

async function runUninstall(opts: UninstallOptions): Promise<void> {
  const projectRoot = process.cwd();
  const artifacts = detectAvatarProjectArtifacts(projectRoot);

  if (!artifacts.hasAnyArtifact) {
    log.info("Project chưa cài Avatar — không có gì để gỡ.");
    return;
  }

  // Show summary.
  printUninstallSummary(projectRoot, artifacts, opts);

  if (opts.dryRun) {
    log.dim("--dry-run: kết thúc, không xóa.");
    return;
  }

  // Confirm.
  if (!opts.yes) {
    const ok = await confirm({
      message: "Tiếp tục gỡ Avatar?",
      default: false,
    });
    if (!ok) {
      log.info("Đã hủy.");
      return;
    }
  }

  // Backup (trừ khi --no-backup).
  let backupPath: string | null = null;
  if (!opts.noBackup) {
    backupPath = await createUninstallBackupSnapshot(projectRoot, artifacts, readCliVersion());
    log.success(`Backup tạo tại: ${backupPath}`);
  }

  // Delete artifacts.
  await executeUninstallDeletion(artifacts, {
    keepSubmodule: opts.keepSubmodule,
    keepHooks: opts.keepHooks,
  });

  // Gỡ binary global avatar đã cài (gitnexus + gitleaks + trivy). User chốt
  // 2026-06-14: gỡ luôn không hỏi. Fail-soft. (Workspace khác sẽ tự cài lại khi sync.)
  uninstallGlobalToolsViaBrew();

  await appendAuditEntry("uninstall", `project=${projectRoot},backup=${backupPath ?? "skipped"}`);

  printUninstallSuccessBox(backupPath);
}

function printUninstallSummary(
  projectRoot: string,
  artifacts: ReturnType<typeof detectAvatarProjectArtifacts>,
  opts: UninstallOptions,
): void {
  log.info(`Project: ${projectRoot}`);
  log.plain("");
  log.plain("Các artifact sẽ gỡ:");
  if (artifacts.claudeDir)
    log.plain(`  ${chalk.red("✗")} ${relative(projectRoot, artifacts.claudeDir) || ".claude/"}`);
  if (artifacts.claudeMd) log.plain(`  ${chalk.red("✗")} CLAUDE.md`);
  if (artifacts.postMergeHook && !opts.keepHooks) {
    log.plain(`  ${chalk.red("✗")} .git/hooks/post-merge`);
  }
  if (artifacts.prePushHook && !opts.keepHooks) {
    log.plain(`  ${chalk.red("✗")} .git/modules/src/hooks/pre-push`);
  }
  if (artifacts.gitignorePath) log.plain(`  ${chalk.yellow("✎")} .gitignore (gỡ Avatar block)`);
  if (artifacts.gitmodulesPath && !opts.keepSubmodule) {
    log.plain(`  ${chalk.yellow("✎")} .gitmodules (gỡ entry .claude/pack)`);
  }
  log.plain(`  ${chalk.red("✗")} Global tools (GỠ KHỎI MÁY): gitnexus, gitleaks, trivy`);
  log.plain(
    `    ${chalk.dim("(dùng chung mọi workspace — workspace khác sẽ tự cài lại khi sync)")}`,
  );
  log.plain("");
  log.plain("Không đụng:");
  log.plain(`  ${chalk.green("✓")} src/ (code khách)`);
  log.plain(`  ${chalk.green("✓")} Git history`);
  log.plain(`  ${chalk.green("✓")} ~/.avatar/config.json (token SSO)`);
  log.plain(`  ${chalk.green("✓")} Secrets trong keychain`);
  log.plain("");
}

function printUninstallSuccessBox(backupPath: string | null): void {
  const lines: string[] = [`${chalk.green("✓")} Avatar đã được gỡ khỏi project`];
  if (backupPath) {
    lines.push("");
    lines.push(`  ${chalk.dim("Backup:")} ${backupPath}`);
    lines.push(`  ${chalk.dim("Restore:")} ${chalk.cyan(`cp -r "${backupPath}"/* .`)}`);
  }
  process.stdout.write(`${boxen(lines.join("\n"), { padding: 1, borderStyle: "round" })}\n`);
}
